// Pure helpers for the TaxId page. Extracted into a .ts file so they can be
// imported by Vitest without the JSX-in-.js gotcha.
//
// All exported helpers must be referentially honest about input, so:
//   - whitespace-only name is treated as empty
//   - missing customer => name is never "dirty" (we don't have a baseline)

export type Customer = {
  name?: string | null;
  email?: string | null;
};

export type TaxTypeOption = {
  value: string;
  label: string;
};

export type TaxId = {
  id: string;
  type: string;
  value: string;
};

export const getNameDirty = (
  nameInput: string,
  customer: Customer | null | undefined
): boolean => {
  if (!customer) return false;
  const trimmed = nameInput.trim();
  if (trimmed === "") return false;
  return trimmed !== (customer.name || "");
};

// Single-tax-ID rule: once the customer has any tax ID, the picker + input
// are hidden until the user deletes it. The form then only governs name
// updates, and any leftover state in `taxValue` MUST be ignored.
export const shouldShowTaxIdInputs = (taxIds: TaxId[]): boolean => {
  return taxIds.length === 0;
};

export const getCanSubmit = (
  nameInput: string,
  taxValue: string,
  customer: Customer | null | undefined,
  taxIds: TaxId[]
): boolean => {
  const nameDirty = getNameDirty(nameInput, customer);
  if (!shouldShowTaxIdInputs(taxIds)) {
    // Tax-ID inputs are hidden — only the name can be submitted.
    return nameDirty;
  }
  const taxIdProvided = taxValue.trim() !== "";
  return taxIdProvided || nameDirty;
};

export const getCtaLabel = (submitting: boolean): string => {
  return submitting ? "Saving..." : "Save";
};

export const getTaxTypeLabel = (
  type: string,
  options: ReadonlyArray<TaxTypeOption>
): string => {
  const match = options.find((o) => o.value === type);
  return match ? match.label : type;
};
